import { useEffect, useState } from 'react'
import { Car } from '../../types/api'
import {
  Box, Button, Paper, Switch, TextField, Typography
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { deleteCar, listCars, updateCar } from '../../api/car'
import { Add, Delete, Edit, Search } from '@mui/icons-material'
import Swal from 'sweetalert2'

export default function CarListPage() {
  const [cars, setCars] = useState<Car[]>([])
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [searchPlate, setSearchPlate] = useState("")
  const navigate = useNavigate()

  // 👉 role จาก localStorage
  const role = (localStorage.getItem("role") || "").toLowerCase()
  const isInspector = role === "inspector"

  const load = async () => {
    try {
      const { list, total } = await listCars({
        take: pageSize,
        skip: page * pageSize,
        plateNumber: searchPlate
      })
      setCars(list)
      setTotal(total)
    } catch (err: any) {
      console.error("Load cars failed", err)
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", err.message, "error")
    }
  }

  console.log(total)

  useEffect(() => { load() }, [])

  const handleSearch = () => {
    setPage(0) // กลับไปหน้าแรก
    load()
  }

  const handleToggle = async (id: number, currentValue: boolean) => {
    if (isInspector) return
    try {
      await updateCar(id, { isDisabled: !currentValue });
      setCars((prev) =>
        prev.map((lm) =>
          lm.id === id ? { ...lm, isDisabled: !currentValue } : lm
        )
      );
    } catch (err) {
      console.error("Toggle failed", err);
    }
  };

  const handleDelete = async (id: number) => {
    if (isInspector) return // ❌ inspector ห้ามลบ

    const result = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: "คุณกำลังจะลบ รถคันนี้",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (result.isConfirmed) {
      try {
        await deleteCar(id);
        await Swal.fire("ลบเรียบร้อย ✅", "", "success");
        load();
      } catch (err: any) {
        Swal.fire("ลบไม่สำเร็จ ❌", err.message, "error");
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'plateNumber', headerName: 'หมายเลขรถ', flex: 1, minWidth: 120 },
    {
      field: 'driverName',
      headerName: 'ชื่อผู้รับผิดชอบ',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => {
        const fullName = [row?.prefix, row?.firstName, row?.lastName].filter(Boolean).join(' ')
        return fullName || '-'
      },
    },
    {
      field: 'gpsDevice',
      headerName: 'GPS หมายเลข',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => row.device?.boxId ?? '-',
    },
    {
      field: "isEngineOn",
      headerName: "สตาร์ทเครื่อง",
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={params.row.isEngineOn}
            disabled
            color="success"
          />
          <Typography variant="body2">
            {params.row.isEngineOn ? "สตาร์ทอยู่" : "ดับเครื่อง"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "isDisabled",
      headerName: "สถานะใช้งาน",
      width: 160,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={!params.row.isDisabled}
            onChange={() =>
              handleToggle(params.row.id, params.row.isDisabled)
            }
            color="warning"
            disabled={isInspector}
          />
          <Typography variant="body2">
            {params.row.isDisabled ? "ปิด" : "เปิด"}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'การจัดการ',
      sortable: false,
      filterable: false,
      width: 220,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={() => navigate(`/cars/${params.row.id}/logs`)}
          >
            ดู Log
          </Button>
          {!isInspector && (
            <>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => navigate(`/cars/edit/${params.row.id}`)}
                startIcon={<Edit />}
              >
                แก้ไข
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDelete(params.row.id)}
              >
                ลบ
              </Button>
            </>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">รายการรถ</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/cars/create')}
          sx={{ backgroundColor: '#FEDC00', color: '#000' }}
          startIcon={<Add />}
          disabled={isInspector} // ❌ inspector ห้ามเพิ่ม
        >
          เพิ่มข้อมูล
        </Button>
      </Box>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid #ddd',
          backgroundColor: '#fafafa',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            label="ค้นหาทะเบียนรถ"
            variant="outlined"
            size="small"
            sx={{ width: 260 }} // ✅ จำกัดความกว้าง
            value={searchPlate}
            onChange={(e) => setSearchPlate(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            startIcon={<Search />}
            sx={{ height: 40 }}
          >
            ค้นหา
          </Button>
        </Box>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ height: 500, width: '100%', p: 1 }}>
         <DataGrid
          rows={cars}
          columns={columns}
          rowCount={total}
          paginationMode="server"
          pageSizeOptions={[10, 15, 25]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page)
            setPageSize(model.pageSize)
          }}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#FEDC00',
              color: '#000',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#FFFDE7',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FFF59D',
            },
          }}
        />
      </Paper>
    </Box>
  )
}
